import type { HelpStep } from './types';
import { anchorNameOf } from './types';

/**
 * DOM helpers shared by the drawer ("locate this control"), the tour (spotlight) and the
 * inspector (click a field, read what it does). Split from the components so all three agree
 * on what "on screen" means — a guide that highlights a hidden element is worse than one that
 * says "not here right now".
 */

/**
 * Marks the guide's own surfaces (the drawer, the tour card, the inspector's overlay) so they
 * are never mistaken for the dialog the user is working in — nor, in the inspector, for a
 * control the user was pointing at.
 */
export const HELP_PANEL_ATTR = 'data-help-panel';

/** The attribute every guide step targets. Built as a string so the checker sees no phantom. */
const ANCHOR_ATTR = 'data-help';

/**
 * Is this node actually showing? Shared so "on screen" means one thing to the drawer, the tour
 * and the inspector.
 */
const isVisible = (el: HTMLElement): boolean => {
  // getClientRects() is empty for `display: none`, an unmounted-but-cached node, and an
  // element inside a collapsed container — cheaper and more honest than reading styles.
  if (el.getClientRects().length === 0) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== 'hidden' && style.display !== 'none';
};

export const findStepElement = (
  selector: string,
  /** Restrict the lookup to one subtree — the open dialog, when there is one. */
  scope: ParentNode = document,
): HTMLElement | null => {
  const el = scope.querySelector<HTMLElement>(selector);
  if (!el) return null;
  return isVisible(el) ? el : null;
};

export const isStepOnScreen = (step: HelpStep, scope: ParentNode = document): boolean =>
  findStepElement(step.selector, scope) !== null;

/**
 * Anchor name → step, built once per guide so the inspector can resolve a pointer position
 * without re-scanning the step list on every mouse move.
 */
export const anchorIndex = (steps: HelpStep[]): Map<string, HelpStep> => {
  const index = new Map<string, HelpStep>();
  for (const step of steps) {
    const anchor = anchorNameOf(step.selector);
    // First wins. Selectors are unique within a guide (`registry.test.ts` proves it), so a
    // collision would be a data bug rather than a precedence question worth expressing here.
    if (anchor && !index.has(anchor)) index.set(anchor, step);
  }
  return index;
};

export interface AnchoredStep {
  step: HelpStep;
  /** The element the step is actually about — the one to outline, not the one clicked. */
  element: HTMLElement;
}

/**
 * The guide entry for whatever the user is pointing at, walking outwards from the exact node
 * under the cursor.
 *
 * A click lands on an `<input>`, a label or an SVG path — never on the wrapper carrying
 * `data-help` — so the anchor has to be found by walking up. Two rules make that walk honest:
 *
 *  - **Innermost documented ancestor wins.** An anchored field inside an anchored section is
 *    the more specific answer, and the specific answer is the one that was asked for.
 *  - **An anchor with no step is walked THROUGH, not stopped at.** Plenty of `data-help`
 *    attributes exist that no guide documents yet; stopping at the first one would answer
 *    "nothing here" while the section around it is documented perfectly well.
 *
 * The guide's own panels resolve to nothing: pointing at the inspector's own card is not a
 * question about the page.
 */
export const stepAtElement = (
  target: EventTarget | null,
  index: Map<string, HelpStep>,
): AnchoredStep | null => {
  if (!(target instanceof Element)) return null;
  if (target.closest(`[${HELP_PANEL_ATTR}]`)) return null;

  let el = target.closest(`[${ANCHOR_ATTR}]`);
  while (el) {
    const step = index.get(el.getAttribute(ANCHOR_ATTR) ?? '');
    if (step && el instanceof HTMLElement) return { step, element: el };
    el = el.parentElement?.closest(`[${ANCHOR_ATTR}]`) ?? null;
  }
  return null;
};

/**
 * The modal dialog the user is currently working in, if any.
 *
 * A tour started while a dialog is open must stay INSIDE it: the page behind the backdrop
 * is not reachable, so spotlighting a filter tab under a modal points at something the
 * user cannot click. The topmost open dialog wins (a dialog can open another), and the
 * guide's own drawer and tour card are excluded — they are not the user's work surface.
 */
export const activeDialogSurface = (): HTMLElement | null => {
  const open = Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"]')).filter(
    (el) => !el.hasAttribute(HELP_PANEL_ATTR) && el.getClientRects().length > 0,
  );
  // Later in document order = rendered on top (portals append).
  return open.length > 0 ? (open[open.length - 1] ?? null) : null;
};

const FLASH_COLOR = '#235592';
const FLASH_MS = 1800;
/** The guide slides back once the outline has faded, plus a beat for the slide itself. */
const YIELD_MS = FLASH_MS + 250;

/**
 * Is the guide's own panel sitting on top of this control?
 *
 * The drawer takes 440px of the right edge, which is exactly where every list page keeps its
 * primary action — so "show me where this is" was outlining controls nobody could see. Vertical
 * `scrollIntoView` cannot fix that; only the panel moving can, and it should only move when it
 * is genuinely in the way.
 */
export const isOccludedByPanel = (el: HTMLElement): boolean => {
  const target = el.getBoundingClientRect();
  if (target.width === 0 && target.height === 0) return false;
  return Array.from(document.querySelectorAll<HTMLElement>(`[${HELP_PANEL_ATTR}]`)).some((panel) => {
    const p = panel.getBoundingClientRect();
    if (p.width === 0 && p.height === 0) return false;
    return (
      target.left < p.right && target.right > p.left && target.top < p.bottom && target.bottom > p.top
    );
  });
};

/**
 * The panel is asked to step aside and to come back — never told how. `dom.ts` owns WHEN
 * (it owns the flash duration); the drawer owns HOW (a React state driving a transform), so
 * nothing here reaches into the component library's internals.
 */
export type YieldHandler = (yielding: boolean) => void;

let yieldTimer = 0;
let yieldingTo: YieldHandler | null = null;

export interface LocateOptions {
  /** Called with `true` when the guide is covering the target, and `false` once it may return. */
  onYield?: YieldHandler;
}

/**
 * Scroll a control into view and flash an outline around it. Returns false when the
 * element is not currently rendered, so the caller can explain why instead of doing
 * nothing (the usual cause: the step lives behind a dialog that is not open).
 */
export const locateElement = (step: HelpStep, options: LocateOptions = {}): boolean => {
  const el = findStepElement(step.selector);
  if (!el) return false;

  if (options.onYield) {
    // While already yielded the panel is off-screen, so its rect no longer says anything about
    // what it covers: a locate during a yield extends it instead of re-measuring. That also
    // makes locating several controls in a row feel deliberate rather than flickery.
    if (yieldingTo !== null || isOccludedByPanel(el)) {
      window.clearTimeout(yieldTimer);
      yieldingTo = options.onYield;
      options.onYield(true);
      yieldTimer = window.setTimeout(() => {
        yieldingTo?.(false);
        yieldingTo = null;
      }, YIELD_MS);
    }
  }

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const previousOutline = el.style.outline;
  const previousOffset = el.style.outlineOffset;
  const previousRadius = el.style.borderRadius;
  el.style.outline = `3px solid ${FLASH_COLOR}`;
  el.style.outlineOffset = '3px';
  if (!previousRadius) el.style.borderRadius = '4px';
  window.setTimeout(() => {
    el.style.outline = previousOutline;
    el.style.outlineOffset = previousOffset;
    el.style.borderRadius = previousRadius;
  }, FLASH_MS);

  return true;
};
